import { useCallback, useEffect, useRef, useState } from 'react';
import { searchable, textSearchable } from '../../lib/payPolicyTemplates/api';
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '../../lib/useDebouncedValue';
import { CursorPage } from './types';

export { SEARCH_DEBOUNCE_MS, useDebouncedValue };

export type SearchRule = (search: string) => boolean;

/** Templates search by their name index's rule, plugins by any text; short of that the list stays unfiltered. */
export const SEARCH_RULES: Record<'templates' | 'text', SearchRule> = { templates: searchable, text: (search: string) => textSearchable(search.trim()) };

export const effectiveSearch = (search: string, rule: SearchRule = SEARCH_RULES.templates) => (rule(search) ? search.trim() : '');

const appendPage = <T>(previous: T[], page: T[]) => [...previous, ...page];

/**
 * A list read a page at a time by cursor, refetched from the start when the search changes.
 * A response for a search the user has already typed past is dropped. A failed first page
 * clears the list, so a new search never lands under an old one; after a failure nothing loads
 * by itself until asked (`autoLoad`), so a failing server is not asked again and again.
 */
export const useCursorList = <T>(fetchPage: (search: string, cursor?: string) => Promise<CursorPage<T>>, search: string, rule: SearchRule, mergePage: (previous: T[], page: T[]) => T[] = appendPage) => {
  const debounced = effectiveSearch(useDebouncedValue(search, SEARCH_DEBOUNCE_MS), rule);
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  const load = useCallback(
    async (cursor: string | undefined, append: boolean) => {
      generation.current += append ? 0 : 1;
      const mine = generation.current;
      setLoading(true);
      setError(null);
      try {
        const page = await fetchPage(debounced, cursor);
        if (mine !== generation.current) return;
        setItems((previous) => (append ? mergePage(previous, page.content) : page.content));
        setNextCursor(page.metadata?.nextCursor ?? null);
        setTotal(page.metadata?.total ?? null);
      } catch (caught) {
        if (mine !== generation.current) return;
        setError(caught instanceof Error ? caught.message : String(caught));
        if (!append) {
          setItems([]);
          setNextCursor(null);
          setTotal(null);
        }
      } finally {
        if (mine === generation.current) setLoading(false);
      }
    },
    [fetchPage, debounced, mergePage]
  );

  useEffect(() => {
    load(undefined, false);
  }, [load]);

  const loadMore = useCallback(() => {
    if (nextCursor) load(nextCursor, true);
  }, [load, nextCursor]);

  const reload = useCallback(() => load(undefined, false), [load]);

  const hasMore = nextCursor !== null;
  // After a failure: the page that failed again, the next one or the first.
  const retry = hasMore ? loadMore : reload;
  return { items, total, loading, error, hasMore, autoLoad: hasMore && !loading && error === null, loadMore, reload, retry, searching: debounced !== '' };
};
